import { Booking } from '../booking/booking.model';
import { Country } from '../country/country.model';
import { List } from 'immutable';
import { ServiceDefinition } from '../service-definition/service-definition.model';
import { StylistJSON } from './stylist-json.model';
import { StylistSaveBody, StylistSerialized } from './stylist-serialized.model';
import { readablePronouns } from '../../utilities/pronouns.util';
import { User } from '../user/user.model';
import { ENV_CONFIG } from 'src/bin/env.config';

export class Stylist extends User<Stylist> {
  constructor(options?: StylistJSON) {
    super(options);

    if (options) {
      this.active = options.active;
      this.administrator = options.administrator;
      this.owner = options.owner;
      this.creationDate = options.creationDate;
      this.isRebook = options.isRebook;
      this.suggestRebookingBeforeCheckout = options.suggestRebookingBeforeCheckout;
      this.stylistKey = options.stylistKey;
      this.confirmApptsManually = options.confirmApptsManually;
      this.calendar = options.calendar;
      this.viewAllCalendars = options.viewAllCalendars;
      this.viewAllClients = options.viewAllClients;
      this.photoUrl = options.photoUrl;
      this.hideDurations = options.hideDurations;
      this.hidePrices = options.hidePrices;
      this.canPush = options.canPush;
      this.settingsControl = options.settingsControl;
      this.canViewReports = options.canViewReports;
      this.canConnectAccounts = Stylist.toFlag(options.canConnectAccounts);
      this.canConnectAccountsEffective = Stylist.toFlag(
        options.canConnectAccountsEffective
      );
      this.cc = options.cc;
      this.allowBookingsUntil = options.allowBookingsUntil;
      this.bookingHorizon = options.bookingHorizon;
      this.sendAppointmentReminderBefore =
        options.sendAppointmentReminderBefore;
      this.bookingInterval = options.bookingInterval;
      this.letClientsRegister = options.letClientsRegister;
      this.letClientsCancel = options.letClientsCancel;
      this.bookingIntro = options.bookingIntro;
      this.notifyForAllStaffActivity = options.notifyForAllStaffActivity;
      this.newBookingNotification = options.newBookingNotification;
      this.editBookingNotification = options.editBookingNotification;
      this.deleteBookingNotification = options.deleteBookingNotification;
      this.waitListRequestNotification = options.waitListRequestNotification;
      this.password = options.password;
      this.abTest = options.abTest;
      this.fbID = options.fbID;
      this.cancellationPolicy = options.cancellationPolicy;
      this.waitingList = options.waitingList;
      this.allowCancellationUntil = options.allowCancellationUntil;
      this.minCancellation = options.minCancellation;
      this.letClientsUseFBLogin = options.letClientsUseFBLogin;
      this.sort = options.sort;
      this.bookingsCount = options.bookingsCount;
      this.clientsCount = options.clientsCount;
      this.pronoun = options.pronoun;
      this.customPronoun = options.customPronoun;
      this.isChatBubbleEnabled = options.isChatBubbleEnabled;
      this.calendarEventDisplayMode = options.calendarEventDisplayMode;
      this.intercomUserHash = options.intercomUserHash;
      this.intercomUserJwt = options.intercomUserJwt;
      this.salonID = options.salonID;
    }

    this.serviceDefinitions = options.serviceDefinitions
      ? options.serviceDefinitions
      : List.of<ServiceDefinition>();
    this.bookings = options.bookings ? options.bookings : List.of<Booking>();
    this.areServiceDetailsLoaded = options.areServiceDetailsLoaded;
    this.cachedServiceDetailsOptionsEl = options.cachedServiceDetailsOptionsEl
      ? options.cachedServiceDetailsOptionsEl
      : '';
  }

  public readonly active: number;

  public readonly administrator: number;
  /**
   * `stylistDetails.owner`. Separate from `administrator`: a salon can grant
   * admin rights to staff who don't own the business, so support needs both.
   * Nullable/absent on sessions restored from before the field existed.
   */
  public readonly owner: number | null | undefined;
  /** When this person joined the salon. See StylistJSON.creationDate. */
  public readonly creationDate: string | Date | null | undefined;
  public readonly isRebook: number;
  public readonly suggestRebookingBeforeCheckout: number;
  public readonly stylistKey: string | undefined;

  public readonly confirmApptsManually: number;

  public readonly calendar: number;

  public readonly viewAllCalendars: number;

  public readonly viewAllClients: number;

  public readonly photoUrl: string;

  public readonly hideDurations: number;

  public readonly hidePrices: number;

  public readonly canPush: number;

  public readonly settingsControl: number;

  public readonly canViewReports: number;

  /**
   * The stored column an administrator grants and revokes. This is the only
   * half of the permission that is ever written.
   */
  public readonly canConnectAccounts: number;

  /**
   * The server's derived `administrator OR canConnectAccounts`. Read-only:
   * parsed and readable, never serialized by toJSON().
   */
  public readonly canConnectAccountsEffective: number;

  public readonly cc: number;

  public readonly allowBookingsUntil: string;

  public readonly bookingHorizon: string;

  public readonly sendAppointmentReminderBefore: number;

  public readonly bookingInterval: number;

  public readonly letClientsRegister: number;

  public readonly letClientsCancel: number;

  public readonly bookingIntro: string;

  public readonly notifyForAllStaffActivity: number;

  public readonly newBookingNotification: number;

  public readonly editBookingNotification: number;

  public readonly deleteBookingNotification: number;

  public readonly waitListRequestNotification: number;

  public readonly serviceDefinitions: List<ServiceDefinition>;

  public readonly bookings: List<Booking>;

  public readonly areServiceDetailsLoaded: boolean;

  public readonly cachedServiceDetailsOptionsEl: string;

  public readonly password: string;

  public readonly abTest: number;

  public readonly fbID: string;

  public readonly cancellationPolicy: string;

  public readonly waitingList: number;

  public readonly allowCancellationUntil: string;

  public readonly minCancellation: string;

  public readonly letClientsUseFBLogin: number;
  public readonly bookingsCount?: number;
  public readonly clientsCount?: number;

  public readonly sort: number;
  public readonly pronoun: number;
  public readonly customPronoun: string;
  public readonly isChatBubbleEnabled: boolean = false;
  public readonly calendarEventDisplayMode?: number;
  public readonly intercomUserHash?: string;
  /** Runtime-only: parsed from the API payload, never serialized by toJSON(). */
  public readonly intercomUserJwt?: string;
  public readonly salonID?: number;

  /**
   * Normalises a wire flag to 0/1. An absent permission reads as not
   * permitted rather than undefined, so it survives toJSON() as a real 0
   * instead of being dropped from the payload.
   */
  private static toFlag(value: unknown): number {
    return Number(value) === 1 ? 1 : 0;
  }

  public static getDefaultProfilePic(): string {
    return `./assets/images/default-profile.png`;
  }

  public static parseStylistData(stylistData): Stylist {
    let country: Country;
    if (stylistData.countryID) {
      country = new Country(stylistData.countryID, stylistData.countryName);
    }

    let bookings: List<Booking> = List([]);

    if (stylistData.bookings) {
      for (const bookingData of stylistData.bookings) {
        bookings = bookings.push(Booking.parseBooking(bookingData));
      }
    }

    return new Stylist({
      id: stylistData.id,
      firstName: stylistData.firstName,
      lastName: stylistData.lastName,
      title: stylistData.title,
      email: stylistData.email,
      phone1: stylistData.phone1,
      phone2: stylistData.phone2,
      address1: stylistData.address1,
      address2: stylistData.address2,
      city: stylistData.city,
      province: stylistData.province,
      postal: stylistData.postal,
      country: country,
      stylistKey: stylistData.stylistKey,
      active: stylistData.active,
      calendar: stylistData.calendar,
      viewAllCalendars: stylistData.viewAllCalendars,
      viewAllClients: stylistData.viewAllClients,
      photoUrl: stylistData.photoUrl,
      hideDurations: stylistData.hideDurations,
      hidePrices: stylistData.hidePrices,
      canPush: stylistData.canPush,
      settingsControl: stylistData.settingsControl,
      canViewReports: stylistData.canViewReports,
      canConnectAccounts: stylistData.canConnectAccounts,
      canConnectAccountsEffective: stylistData.canConnectAccountsEffective,
      cc: stylistData.cc,
      confirmApptsManually: stylistData.confirmApptsManually,
      administrator: stylistData.administrator,
      owner: stylistData.owner,
      creationDate: stylistData.creationDate,
      isRebook: stylistData.isRebook,
      suggestRebookingBeforeCheckout: stylistData.suggestRebookingBeforeCheckout,
      allowBookingsUntil: stylistData.allowBookingsUntil,
      bookingHorizon: stylistData.bookingHorizon,
      sendAppointmentReminderBefore: stylistData.sendAppointmentReminderBefore,
      bookingInterval: stylistData.bookingInterval,
      letClientsRegister: stylistData.letClientsRegister,
      letClientsCancel: stylistData.letClientsCancel,
      bookingIntro: stylistData.bookingIntro,
      notifyForAllStaffActivity: stylistData.notifyForAllStaffActivity,
      newBookingNotification: stylistData.newBookingNotification,
      editBookingNotification: stylistData.editBookingNotification,
      deleteBookingNotification: stylistData.deleteBookingNotification,
      waitListRequestNotification: stylistData.waitListRequestNotification,
      bookings: bookings,
      password: stylistData.password,
      abTest: stylistData.abTest,
      fbID: stylistData.fbID,
      cancellationPolicy: stylistData.cancellationPolicy,
      waitingList: stylistData.waitingList,
      allowCancellationUntil: stylistData.allowCancellationUntil,
      minCancellation: stylistData.minCancellation,
      letClientsUseFBLogin: stylistData.letClientsUseFBLogin,
      sort: stylistData.sort,
      bookingsCount: stylistData.bookingsCount,
      clientsCount: stylistData.clientsCount,
      pronoun: stylistData.pronoun,
      customPronoun: stylistData.customPronoun,
      intercomUserHash: stylistData.intercomUserHash,
      intercomUserJwt: stylistData.intercomUserJwt,
      isChatboxEnabled: true,
      isChatBubbleEnabled: stylistData.isChatBubbleEnabled,
      calendarEventDisplayMode: stylistData.calendarEventDisplayMode,
      salonID: stylistData.salonID,
    });
  }

  public setID(newID: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.id = newID;
    return new Stylist(data);
  }

  public setFirstName(firstName: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.firstName = firstName;
    return new Stylist(data);
  }

  public setLastName(lastName: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.lastName = lastName;
    return new Stylist(data);
  }

  public setTitle(title: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.title = title;
    return new Stylist(data);
  }

  public setEmail(email: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.email = email;
    return new Stylist(data);
  }

  public setPhone1(phone1: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.phone1 = phone1;
    return new Stylist(data);
  }

  public setPhone2(phone2: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.phone2 = phone2;
    return new Stylist(data);
  }

  public setAddress1(address1: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.address1 = address1;
    return new Stylist(data);
  }

  public setAddress2(address2: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.address2 = address2;
    return new Stylist(data);
  }

  public setCity(city: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.city = city;
    return new Stylist(data);
  }

  public setProvince(province: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.province = province;
    return new Stylist(data);
  }

  public setPostal(postal: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.postal = postal;
    return new Stylist(data);
  }

  public setCountry(newCountry: Country): Stylist {
    const data: StylistJSON = this.toJSON();
    data.country = newCountry.clone();
    return new Stylist(data);
  }

  public isActive(): boolean {
    return this.active === 1;
  }

  public setActive(active: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.active = active;
    return new Stylist(data);
  }

  public isAdministrator(): boolean {
    return this.administrator === 1;
  }

  /**
   * True only for the salon's owner. `=== 1` matches isAdministrator() and the
   * API's own check, and reads false for the null the column allows.
   */
  public isOwner(): boolean {
    return this.owner === 1;
  }

  /** When this person joined the salon, or undefined if the API didn't say. */
  public getCreationDate(): Stylist['creationDate'] {
    return this.creationDate;
  }

  public setadministrator(administrator: boolean): Stylist {
    const data: StylistJSON = this.toJSON();
    data.administrator = administrator ? 1 : 0;
    return new Stylist(data);
  }

  public getIsRebook(): number {
    return this.isRebook;
  }

  public setIsRebook(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.isRebook = value;
    return new Stylist(data);
  }

  public getsuggestRebookingBeforeCheckout(): number {
    return this.suggestRebookingBeforeCheckout;
  }

  public setsuggestRebookingBeforeCheckout(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.suggestRebookingBeforeCheckout = value;
    return new Stylist(data);
  }

  public getStylistKey(): string {
    return this.stylistKey;
  }

  public getConfirmApptsManually(): number {
    return this.confirmApptsManually;
  }

  public setConfirmApptsManually(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.confirmApptsManually = value;
    return new Stylist(data);
  }

  public getCalendar(): number {
    return this.calendar;
  }

  public setCalendar(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.calendar = value;
    return new Stylist(data);
  }

  public getViewAllCalendars(): number {
    return this.viewAllCalendars;
  }

  public setViewAllCalendars(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.viewAllCalendars = value;
    return new Stylist(data);
  }

  public getViewAllClients(): number {
    return this.viewAllClients;
  }

  public setViewAllClients(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.viewAllClients = value;
    return new Stylist(data);
  }

  public getSettingsControl(): number {
    return this.settingsControl;
  }

  public setSettingsControl(settingsControl: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.settingsControl = settingsControl;
    return new Stylist(data);
  }

  public getPhotoUrl(): string {
    return this.photoUrl;
  }

  public hasPhoto(): boolean {
    const photo = this.getPhotoUrl();
    return photo && photo !== '';
  }

  public getFullPhotoUrl(): string {
    const photo: string = this.getPhotoUrl();

    if (!this.hasPhoto()) {
      return Stylist.getDefaultProfilePic();
    }

    return ENV_CONFIG.PHOTO_ROOT + '/' + this.photoUrl;
  }

  public getFullThumbnailUrl(): string {
    const photo: string = this.getPhotoUrl();

    if (!this.hasPhoto()) {
      return Stylist.getDefaultProfilePic();
    }

    return ENV_CONFIG.STAFF_THUMBNAIL_PHOTO_ROOT + '/' + this.photoUrl;
  }

  public getHideDurations(): number {
    return this.hideDurations;
  }

  public getHidePrices(): number {
    return this.hidePrices;
  }

  public getCanPush(): number {
    return this.canPush;
  }

  public setCanPush(value: number) {
    const data: StylistJSON = this.toJSON();
    data.canPush = value;
    return new Stylist(data);
  }

  public getCanViewReports(): number {
    return this.canViewReports;
  }

  public setCanViewReports(value: number) {
    const data: StylistJSON = this.toJSON();
    data.canViewReports = value;
    return new Stylist(data);
  }

  /**
   * May this user take part in connectable accounts?
   *
   * The one definition, because two surfaces ask it — the route guard and the
   * side-menu entry — and they must never disagree: a menu that offers a page
   * the guard bounces, or hides one it would admit, is a bug either way.
   *
   * Takes `unknown` and fails closed on anything that cannot answer. A user
   * object restored from an older app version has no such accessor, and must
   * not throw its way into an implicit allow.
   */
  public static mayConnectAccounts(user: unknown): boolean {
    const candidate = user as { getCanConnectAccountsEffective?: () => number };
    return (
      !!candidate &&
      typeof candidate.getCanConnectAccountsEffective === 'function' &&
      candidate.getCanConnectAccountsEffective() === 1
    );
  }

  public getCanConnectAccounts(): number {
    return this.canConnectAccounts;
  }

  public setCanConnectAccounts(value: number) {
    const data: StylistJSON = this.toJSON();
    data.canConnectAccounts = value;
    return new Stylist(data);
  }

  /**
   * May this stylist take part in connectable accounts at all? Administrators
   * hold the permission implicitly; everyone else needs the granted column.
   *
   * Derived here as well as on the server because toJSON() drops the wire
   * field on purpose, so a restored session would otherwise lose it.
   */
  public getCanConnectAccountsEffective(): number {
    return this.canConnectAccountsEffective === 1 ||
      this.administrator === 1 ||
      this.canConnectAccounts === 1
      ? 1
      : 0;
  }

  public getCc(): number {
    return this.cc;
  }

  public setCc(cc: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.cc = cc;
    return new Stylist(data);
  }

  public getAllowBookingsUntil(): string {
    return this.allowBookingsUntil;
  }

  public setAllowBookingsUntil(value: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.allowBookingsUntil = value;
    return new Stylist(data);
  }

  public getBookingHorizon(): string {
    return this.bookingHorizon;
  }

  public setBookingHorizon(value: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.bookingHorizon = value;
    return new Stylist(data);
  }

  public getSendAppointmentReminderBefore(): number {
    return this.sendAppointmentReminderBefore;
  }

  public setSendAppointmentReminderBefore(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.sendAppointmentReminderBefore = value;
    return new Stylist(data);
  }

  public getBookingInterval(): number {
    return this.bookingInterval;
  }

  public setBookingInterval(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.bookingInterval = value;
    return new Stylist(data);
  }

  public getLetClientsRegister(): number {
    return this.letClientsRegister;
  }

  public setLetClientsRegister(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.letClientsRegister = value;
    return new Stylist(data);
  }

  public getLetClientsCancel(): number {
    return this.letClientsCancel;
  }

  public setLetClientsCancel(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.letClientsCancel = value;
    return new Stylist(data);
  }

  public getBookingIntro(): string {
    return this.bookingIntro;
  }

  public setBookingIntro(value: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.bookingIntro = value;
    return new Stylist(data);
  }

  public getNotifyForAllStaffActivity(): number {
    return this.notifyForAllStaffActivity;
  }

  public setNotifyForAllStaffActivity(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.notifyForAllStaffActivity = value;
    return new Stylist(data);
  }

  public getNewBookingNotification(): number {
    return this.newBookingNotification;
  }

  public setNewBookingNotification(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.newBookingNotification = value;
    return new Stylist(data);
  }

  public getEditBookingNotification(): number {
    return this.editBookingNotification;
  }

  public setEditBookingNotification(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.editBookingNotification = value;
    return new Stylist(data);
  }

  public getDeleteBookingNotification(): number {
    return this.deleteBookingNotification;
  }

  public setDeleteBookingNotification(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.deleteBookingNotification = value;
    return new Stylist(data);
  }

  public getWaitListRequestNotification(): number {
    return this.waitListRequestNotification;
  }

  public setWaitListRequestNotification(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.waitListRequestNotification = value;
    return new Stylist(data);
  }

  public getBookings(): List<Booking> {
    return this.bookings;
  }

  public areServicesLoaded(): boolean {
    return this.areServiceDetailsLoaded;
  }

  public getCachedServiceDetailsOptionsEl() {
    return this.cachedServiceDetailsOptionsEl;
  }

  public getServiceDefinitions(): List<ServiceDefinition> {
    return this.serviceDefinitions;
  }

  public setPassword(password: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.password = password;
    return new Stylist(data);
  }

  public setAbTest(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.abTest = value;
    return new Stylist(data);
  }

  public setFbID(value: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.fbID = value;
    return new Stylist(data);
  }

  public setCancellationPolicy(value: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.cancellationPolicy = value;
    return new Stylist(data);
  }

  public setHideDurations(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.hideDurations = value;
    return new Stylist(data);
  }

  public setHidePrices(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.hidePrices = value;
    return new Stylist(data);
  }

  public setWaitingList(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.waitingList = value;
    return new Stylist(data);
  }

  public setallowCancellationUntil(value: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.allowCancellationUntil = value;
    return new Stylist(data);
  }

  public setLetClientsUseFBLogin(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.letClientsUseFBLogin = value;
    return new Stylist(data);
  }

  public setMinCancellation(value: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.minCancellation = value;
    return new Stylist(data);
  }

  public setSort(value: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.sort = value;
    return new Stylist(data);
  }
  public getPronoun(): number {
    return this.pronoun;
  }
  public setPronoun(pronoun: number): Stylist {
    const data: StylistJSON = this.toJSON();
    data.pronoun = pronoun;
    return new Stylist(data);
  }
  public getCustomPronoun(): string {
    return this.customPronoun;
  }

  /**
   * Pronouns as a person reads them — see readablePronouns() for the code map.
   */
  public getReadablePronouns(): string {
    return readablePronouns(this.pronoun, this.customPronoun);
  }

  public setCustomPronoun(customPronoun: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.customPronoun = customPronoun;
    return new Stylist(data);
  }

  public getSort(): number {
    return this.sort;
  }

  public getBookingsCount(): number {
    return this.bookingsCount;
  }

  public getClientsCount(): number {
    return this.clientsCount;
  }

  public getStylistServicesCount(): number {
    return this.getServiceDefinitions().size;
  }

  public setIntercomUserHash(hash: string): Stylist {
    const data: StylistJSON = this.toJSON();
    data.intercomUserHash = hash;
    return new Stylist(data);
  }

  public toJSON(): StylistJSON {
    return Object.assign(super.toJSON(), {
      active: this.active,
      administrator: this.administrator,
      owner: this.owner,
      creationDate: this.creationDate,
      isRebook: this.getIsRebook(),
      suggestRebookingBeforeCheckout: this.getsuggestRebookingBeforeCheckout(),
      stylistKey: this.getStylistKey(),
      confirmApptsManually: this.getConfirmApptsManually(),
      calendar: this.getCalendar(),
      viewAllCalendars: this.getViewAllCalendars(),
      viewAllClients: this.getViewAllClients(),
      photoUrl: this.getPhotoUrl(),
      hideDurations: this.getHideDurations(),
      hidePrices: this.getHidePrices(),
      canPush: this.getCanPush(),
      settingsControl: this.getSettingsControl(),
      canViewReports: this.canViewReports,
      // Only the stored column is serialized. canConnectAccountsEffective is
      // deliberately omitted here rather than stripped in serialize(): every
      // setX() helper and clone() rebuilds a Stylist from toJSON(), and the
      // derived value must not survive that copy. (owner and creationDate take
      // the other route — kept here so copies and the stored session keep them,
      // dropped in serialize() so they never reach a save body.) Emitting the
      // derived value would write an administrator's implicit permission into
      // their own column on an ordinary save.
      canConnectAccounts: this.getCanConnectAccounts(),
      cc: this.getCc(),
      allowBookingsUntil: this.getAllowBookingsUntil(),
      bookingHorizon: this.getBookingHorizon(),
      sendAppointmentReminderBefore: this.getSendAppointmentReminderBefore(),
      bookingInterval: this.getBookingInterval(),
      letClientsRegister: this.getLetClientsRegister(),
      letClientsCancel: this.getLetClientsCancel(),
      bookingIntro: this.getBookingIntro(),
      notifyForAllStaffActivity: this.getNotifyForAllStaffActivity(),
      newBookingNotification: this.getNewBookingNotification(),
      editBookingNotification: this.getEditBookingNotification(),
      deleteBookingNotification: this.getDeleteBookingNotification(),
      waitListRequestNotification: this.getWaitListRequestNotification(),
      pronoun: this.getPronoun(),
      customPronoun: this.getCustomPronoun(),
      serviceDefinitions: this.serviceDefinitions,
      areServiceDetailsLoaded: this.areServiceDetailsLoaded,
      cachedServiceDetailsOptionsEl: this.cachedServiceDetailsOptionsEl,
      password: this.password,
      abTest: this.abTest,
      fbID: this.fbID,
      cancellationPolicy: this.cancellationPolicy,
      waitingList: this.waitingList,
      allowCancellationUntil: this.allowCancellationUntil,
      letClientsUseFBLogin: this.letClientsUseFBLogin,
      minCancellation: this.minCancellation,
      sort: this.sort,
      bookingsCount: this.bookingsCount,
      clientsCount: this.clientsCount,
      isChatBubbleEnabled: this.isChatBubbleEnabled,
      calendarEventDisplayMode: this.calendarEventDisplayMode,
      // intercomUserHash persists so restored sessions can boot with the
      // fallback; intercomUserJwt is deliberately omitted (runtime-only).
      intercomUserHash: this.intercomUserHash,
      salonID: this.salonID,
    });
  }

  public clone(): Stylist {
    return new Stylist(this.toJSON());
  }

  /**
   * The full session snapshot persisted to localStorage. Unlike serialize()
   * it keeps server-owned fields such as `owner`, because the restored
   * session is rebuilt from this and Intercom reports `is-owner` on reload.
   */
  public toStoredSession(): StylistSerialized {
    return {
      countryID: this.getCountry() ? this.getCountry().getID() : null,
      ...this.toJSON()
    };
  }

  /**
   * The body sent to the API on save. Both server-owned fields are dropped.
   * `owner` is the one that bites today: the API assigns
   * `stylistDetails.owner` from `params["owner"]` and its isSet() check is
   * `!== undefined`, so a null owner in a save body writes NULL over the flag
   * rather than being ignored. `creationDate` is inert — no stylist route
   * reads it from params — but it is equally server-owned, and leaving it in
   * would read as "this one is meant to be sent". One rule, both fields.
   *
   * `intercomUserHash` goes the same way. It is the stylist's Intercom
   * identity HMAC, no stylist route ever reads it from a request body, and it
   * was riding along in every save purely because serialize() spread toJSON().
   * A credential the server ignores has no business in a request log.
   */
  public serialize(): StylistSaveBody {
    const {
      owner: _owner,
      creationDate: _creationDate,
      intercomUserHash: _intercomUserHash,
      ...json
    } = this.toStoredSession();
    return json;
  }
}
